/**
 * WHAT A READER WOULD SEE OF THIS PROFILE - the one place the preview decides it (design 24, T28).
 *
 * The author's own profile (`getOwnProfile`) carries everything, the private block included; the
 * public projection readers get (`PublicAuthor`, magazine-api.yaml) carries only what is public, and
 * "a public reader sees the field or does not" (`FieldVisibility`, authors-api.yaml). This function
 * is that boundary, restated for the preview, so the page draws from its answer and never from the
 * profile itself:
 *   - always public: the author name, the photo, the short bio and "Anything else" (`about`) - they
 *     have no switch, and that absence is the contract (../../features/application/domain/limits.ts);
 *   - behind their switch: the `FieldVisibility` fields, a switch not recorded taking its ruled
 *     default (public, except the time zone) - and since design 25 the years of practice and the
 *     primary location (its label only), which take the time zone's place: the zone itself is no
 *     longer drawn;
 *   - one by one: the topics and the links, each with its own `isPublic`; the main areas always;
 *   - the zodiac signs only while their own switch is on (`computedSignsPublic`);
 *   - never: the private block, the e-mail address, the rate, when you can write.
 *
 * TO ADD A FIELD: a member here, read under its switch, and its line in ./ProfilePreview.tsx.
 * Nothing else draws from the profile.
 */
import type { CategoryList, OwnProfile } from '../../features/application/contract';
import { SOCIAL_LINK_KINDS, VISIBILITY_FIELDS } from '../../features/application/domain/limits';
import type { TextVisibilityKey, VisibilityKey } from '../../features/application/domain/limits';

export interface PublicLink {
  /** The kind's code, `website`; the page names it in the cabinet's language where it can. */
  readonly kind: string;
  /** The kind's name in English, "Website", "Instagram". */
  readonly label: string;
  readonly url: string;
}

export interface PublicSigns {
  readonly sun: string;
  readonly moon: string | null;
  readonly rising: string | null;
}

export interface PublicView {
  readonly displayName: string | null;
  readonly avatarUrl: string | null;
  readonly bioShort: string | null;
  readonly about: string | null;
  readonly headline: string | null;
  readonly bioLong: string | null;
  readonly experience: string | null;
  readonly approach: string | null;
  readonly education: string | null;
  readonly pronouns: string | null;
  /** "12 years of practice" (design 25, T25); 0 is shown too. */
  readonly yearsOfPractice: number | null;
  /** "Based in": the picked place's label (design 25, T27). */
  readonly primaryLocation: string | null;
  /** The main areas readers find authors by, named from the catalogue, in the author's order. */
  readonly mainAreas: readonly string[];
  /** "Writes & researches in": the topics, as the author keeps them. */
  readonly topics: readonly string[];
  readonly links: readonly PublicLink[];
  readonly signs: PublicSigns | null;
}

/** Text worth drawing, or nothing: a blank field is a field readers do not see. */
function filled(value: string | null | undefined): string | null {
  return value === undefined || value === null || value.trim() === '' ? null : value.trim();
}

/** Whether readers see a switched field: the recorded switch, else its ruled default. */
function isShown(profile: OwnProfile, key: VisibilityKey): boolean {
  return (
    profile.visibility[key] ??
    VISIBILITY_FIELDS.find((field) => field.key === key)?.defaultPublic ??
    false
  );
}

function behindSwitch(profile: OwnProfile, key: TextVisibilityKey): string | null {
  return isShown(profile, key) ? filled(profile[key]) : null;
}

/** `cancer` -> `Cancer`. The codes are the zodiac lookup's own (`zodiac_sign.code`). */
function signWords(code: string | null | undefined): string | null {
  const value = filled(code);
  return value === null ? null : `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
}

function linkLabel(kind: string): string {
  return SOCIAL_LINK_KINDS.find((known) => known.code === kind)?.label ?? signWords(kind) ?? kind;
}

/**
 * `categories` is the catalogue the main areas are named from. While it is missing - it failed to
 * load - an area is named by its code rather than left out, because readers do see it.
 */
export function publicViewOf(
  profile: OwnProfile,
  categories: CategoryList | undefined,
): PublicView {
  const sun = profile.signs.computedSignsPublic ? signWords(profile.signs.sunSign) : null;
  return {
    displayName: filled(profile.displayName),
    avatarUrl: filled(profile.avatarUrl),
    bioShort: filled(profile.bioShort),
    about: filled(profile.about),
    headline: behindSwitch(profile, 'headline'),
    bioLong: behindSwitch(profile, 'bioLong'),
    experience: behindSwitch(profile, 'experience'),
    approach: behindSwitch(profile, 'approach'),
    education: behindSwitch(profile, 'education'),
    pronouns: behindSwitch(profile, 'pronouns'),
    // Shown whenever a number is set: it has no switch (the owner, 2026-10-19).
    yearsOfPractice: profile.yearsOfPractice ?? null,
    primaryLocation: isShown(profile, 'primaryLocation')
      ? filled(profile.primaryLocation?.label)
      : null,
    // Always public, as the reader's page shows them (migration 0118).
    mainAreas: profile.expertise.map(
      (entry) =>
        categories?.items.find((item) => item.code === entry.categoryCode)?.label ??
        entry.categoryCode,
    ),
    topics: profile.tags
      .filter((tag) => tag.isPublic)
      .map((tag) => filled(tag.tagText))
      .filter((text): text is string => text !== null),
    links: [...profile.links]
      .filter((link) => link.isPublic && filled(link.url) !== null)
      .sort(
        (a, b) =>
          (a.sortOrder ?? Number.MAX_SAFE_INTEGER) - (b.sortOrder ?? Number.MAX_SAFE_INTEGER),
      )
      .map((link) => ({
        kind: link.linkKind,
        label: linkLabel(link.linkKind),
        url: link.url.trim(),
      })),
    signs:
      sun === null
        ? null
        : {
            sun,
            moon: signWords(profile.signs.moonSign),
            rising: signWords(profile.signs.risingSign),
          },
  };
}
